import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bot, Loader2, Send, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ExperimentProtocolPreview from "./ExperimentProtocolPreview";

interface ProtocolAiAssistantProps {
  currentJson: object;
  onJsonUpdate: (newJson: object) => void;
}

const ProtocolAiAssistant: React.FC<ProtocolAiAssistantProps> = ({
  currentJson,
  onJsonUpdate
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [conversation, setConversation] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [pendingJson, setPendingJson] = useState<any | null>(null);

  const aiMutation = useMutation({
    mutationFn: async (userPrompt: string) => {
      const messages = [
        {
          role: 'system' as const,
          content: `You are an AI assistant that helps modify experiment protocol JSON. The current protocol is:

${JSON.stringify(currentJson, null, 2)}

You have access to a function to search for products. When modifying the protocol, ensure it follows this schema:
{
  "days": [
    {
      "title": "Day X",
      "description": "Day description", 
      "actions": [
        {
          "meta": {
            "duration": "HH:MM:SS", // for fasting/satiety actions
            "model_time": "HH:MM:SS", // when action should occur
            "product_ids": ["id1", "id2"] // for product actions
          },
          "type": "fasting|product|satiety",
          "title": "Action title",
          "description": "Action description"
        }
      ]
    }
  ]
}

Please respond with the updated JSON protocol based on the user's request. If you need to search for products, use the search_products function.`
        },
        ...conversation,
        {
          role: 'user' as const,
          content: `Update the json test protocol according to these following changes: ${userPrompt}`
        }
      ];

      const { data: response, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          messages,
          model: 'gpt-4o-mini',
          temperature: 0.3,
          functions: [
            {
              name: 'search_products',
              description: 'Search for products in the Tastermonial database',
              parameters: {
                type: 'object',
                properties: {
                  query: {
                    type: 'string',
                    description: 'Search query - if numeric, searches fdc_id and gtin_upc; if text, searches brand_owner, brand_name, description, and title'
                  },
                  page: {
                    type: 'integer',
                    description: 'Page number (1-based)',
                    default: 1
                  },
                  page_size: {
                    type: 'integer', 
                    description: 'Number of items per page',
                    default: 25
                  }
                },
                required: ['query']
              }
            }
          ],
          function_call: 'auto'
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to get AI response');
      }

      return response.content;
    },
    onSuccess: (response) => {
      let jsonContent = null;
      
      try {
        // First try to parse the response directly as JSON
        jsonContent = JSON.parse(response);
      } catch (error) {
        // If that fails, try to extract JSON from markdown code blocks
        const jsonBlockMatch = response.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
        if (jsonBlockMatch && jsonBlockMatch[1]) {
          try {
            jsonContent = JSON.parse(jsonBlockMatch[1].trim());
          } catch (parseError) {
            // Still not valid JSON, continue to add as conversation
          }
        }
      }
      
      if (jsonContent) {
        setPendingJson(jsonContent);
        setConversation(prev => [...prev, 
          { role: 'user', content: prompt },
          { role: 'assistant', content: 'Protocol preview ready. Review and click Apply to update.' }
        ]);
        setPrompt("");
      } else {
        // If no valid JSON found, add it as a conversation message
        setConversation(prev => [...prev, 
          { role: 'user', content: prompt },
          { role: 'assistant', content: response }
        ]);
        setPrompt("");
      }
    },
    onError: (error) => {
      toast.error(`AI Assistant error: ${error.message}`);
      setConversation(prev => [...prev, 
        { role: 'user', content: prompt },
        { role: 'assistant', content: `Error: ${error.message}` }
      ]);
      setPrompt("");
    },
  });

  const handleSubmit = () => {
    if (!prompt.trim()) return;
    aiMutation.mutate(prompt);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleApply = () => {
    if (pendingJson) {
      onJsonUpdate(pendingJson);
      setPendingJson(null);
      toast.success("Protocol updated by AI assistant");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="ml-2 bg-primary/10 border-primary/20 text-primary hover:bg-primary/20 hover:border-primary/30"
          title="AI Protocol Assistant"
        >
          <Bot className="h-4 w-4 mr-2" />
          Update with AI
        </Button>
      </DialogTrigger>
      <DialogContent className={`${pendingJson ? 'w-[90vw] max-w-6xl' : 'w-[500px]'} max-h-[600px] flex flex-col`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            AI Protocol Assistant
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Describe the changes you want to make to the experiment protocol.
          </p>
        </DialogHeader>
        
        <div className={`flex-1 min-h-0 ${pendingJson ? 'grid grid-cols-2 gap-4' : 'flex flex-col'}`}>
          <div className="flex flex-col min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-2">
              {conversation.map((message, index) => (
                <div key={index} className={`p-3 rounded-lg text-sm ${
                  message.role === 'user' 
                    ? 'bg-primary/10 ml-4' 
                    : 'bg-muted mr-4'
                }`}>
                  <div className="font-medium text-xs mb-1 opacity-70">
                    {message.role === 'user' ? 'You' : 'AI Assistant'}
                  </div>
                  <div className="whitespace-pre-wrap">{message.content}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-4">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., 'Add a product action for chocolate bars', 'Change fasting duration to 12 hours', 'Add a second day with satiety logging'"
                className="flex-1 resize-none"
                rows={2}
              />
              <Button
                onClick={handleSubmit}
                disabled={aiMutation.isPending || !prompt.trim()}
                size="sm"
                className="self-end"
              >
                {aiMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {pendingJson && (
            <div className="flex flex-col min-h-0 border-l pl-4">
              <h3 className="text-sm font-medium mb-2">Preview</h3>
              <div className="flex-1 min-h-0 overflow-auto border rounded-md bg-background p-3">
                <ExperimentProtocolPreview protocolData={pendingJson} />
              </div>
              <div className="pt-3">
                <Button onClick={handleApply} className="w-full">
                  <Check className="h-4 w-4 mr-2" />
                  Apply Changes
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProtocolAiAssistant;